const DB_NAME = "glide-wallet";
const STORE_NAME = "keys";
const KEY_ID = "encryption-key";

// localStorage fallback for environments where IndexedDB is unavailable
// (e.g. iOS WebKit under Lockdown Mode, content blockers, some webviews).
const LS_KEY_ID = "glide.wallet.enc.key";

// Reading the bare `indexedDB` identifier throws a ReferenceError on WebKit
// when the global is absent, so resolve it defensively and never throw.
function getIndexedDB(): IDBFactory | null {
  try {
    return typeof indexedDB !== "undefined"
      ? indexedDB
      : (window.indexedDB ?? null);
  } catch {
    return null;
  }
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const idb = getIndexedDB();
    if (!idb) {
      reject(new Error("IndexedDB is unavailable"));
      return;
    }
    try {
      const req = idb.open(DB_NAME, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(STORE_NAME);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    } catch (e) {
      // `open` can throw synchronously (e.g. SecurityError) even when defined.
      reject(e);
    }
  });
}

// Reads the existing IndexedDB key without ever generating one. Returns null
// when IndexedDB is absent or no key has been stored yet, so callers can fall
// through to an already-existing fallback key instead of minting a fresh key
// that would not match previously-encrypted data.
async function loadIDBKey(): Promise<CryptoKey | null> {
  if (!getIndexedDB()) return null;
  const db = await openDB();
  const existing = await new Promise<CryptoKey | undefined>(
    (resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const req = tx.objectStore(STORE_NAME).get(KEY_ID);
      req.onsuccess = () => resolve(req.result as CryptoKey | undefined);
      req.onerror = () => reject(req.error);
    },
  );
  return existing ?? null;
}

async function createIDBKey(): Promise<CryptoKey> {
  const db = await openDB();
  const key = await crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );

  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put(key, KEY_ID);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });

  return key;
}

async function loadFallbackKey(): Promise<CryptoKey | null> {
  const raw = window.localStorage.getItem(LS_KEY_ID);
  if (!raw) return null;
  try {
    const jwk = JSON.parse(raw) as JsonWebKey;
    return await crypto.subtle.importKey(
      "jwk",
      jwk,
      { name: "AES-GCM" },
      true,
      ["encrypt", "decrypt"],
    );
  } catch {
    return null;
  }
}

async function createFallbackKey(): Promise<CryptoKey> {
  // Extractable so it can be persisted as JWK in localStorage. This is weaker
  // than the non-extractable IndexedDB key (it sits next to the ciphertext),
  // but keeps the wallet working where IndexedDB is unavailable.
  const key = await crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"],
  );
  const jwk = await crypto.subtle.exportKey("jwk", key);
  window.localStorage.setItem(LS_KEY_ID, JSON.stringify(jwk));
  return key;
}

export async function getOrCreateEncryptionKey(): Promise<CryptoKey> {
  // Prefer any key that already exists over generating a new one. A wallet is
  // encrypted with exactly one key, stored in whichever location was available
  // at creation time, so creating a fresh key when one already exists (in
  // either store) would orphan the encrypted private keys and freeze funds.

  // 1. Existing IndexedDB key (read-only; never mints one on a miss).
  try {
    const idbExisting = await loadIDBKey();
    if (idbExisting) return idbExisting;
  } catch {
    // IndexedDB present but unreadable; fall through to the fallback key.
  }

  // 2. Existing localStorage fallback key.
  const fallbackExisting = await loadFallbackKey();
  if (fallbackExisting) return fallbackExisting;

  // 3. No key anywhere yet: create one, preferring the secure IndexedDB path.
  if (getIndexedDB()) {
    try {
      return await createIDBKey();
    } catch {
      // Fall through to the localStorage fallback below.
    }
  }
  return await createFallbackKey();
}

export async function encryptBytes(
  key: CryptoKey,
  data: Uint8Array,
): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, data),
  );
  const combined = new Uint8Array(iv.length + ciphertext.length);
  combined.set(iv);
  combined.set(ciphertext, iv.length);
  return btoa(String.fromCharCode(...combined));
}

export async function decryptBytes(
  key: CryptoKey,
  encoded: string,
): Promise<Uint8Array> {
  const combined = Uint8Array.from(atob(encoded), (c) => c.charCodeAt(0));
  const iv = combined.slice(0, 12);
  const ciphertext = combined.slice(12);
  const plaintext = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv },
    key,
    ciphertext,
  );
  return new Uint8Array(plaintext);
}

export async function deleteEncryptionKey(): Promise<void> {
  window.localStorage.removeItem(LS_KEY_ID);

  if (!getIndexedDB()) return;

  try {
    const db = await openDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      tx.objectStore(STORE_NAME).delete(KEY_ID);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // IndexedDB present but unusable; the localStorage key is already cleared.
  }
}
